/**
 * Shared primitives for the per-block-type extension renderers.
 * Btn / H1 / Eyebrow lifted from the original PremiumSectionRendererExt.
 */
import React from 'react';
import { getWbButtonAttrs } from '../../sqs/WbButton';
import { InlineText } from '../../content-editor/DashboardParts';

// Iter 175 — Section Audit. Inline-edit primitive for premium ext blocks.
// When the renderer was invoked with onEdit available, text fields become
// click-to-edit; otherwise we fall back to a plain element so public-view
// rendering keeps working. Supports nested paths via dotted `field`
// (e.g. "info.phone", "items.0.title", "buttons.1.text") — onEdit's
// caller in useContentEditor resolves the dotted path before saving.
export const E = ({ block, onEdit, field, value, tag = 'span', className, style, placeholder = 'Click to edit' }) => {
  if (onEdit) {
    return (
      <InlineText
        blockId={block.id}
        onEdit={onEdit}
        field={field}
        value={value ?? ''}
        tag={tag}
        className={className}
        fieldStyle={style}
        placeholder={placeholder}
      />
    );
  }
  return React.createElement(tag, { className, style }, value);
};

// Iter 175 — Editable Button. Renders the same chip-style anchor as
// `Btn` below, but when an `onEdit` callback is wired through the
// extension chain the label becomes an inline ContentEditable and the
// destination link can be edited inline as well via a side editor.
export const EBtn = ({ btn, i, theme, onEdit, block, color, primary, sharp, field = 'buttons' }) => {
  const attrs = getWbButtonAttrs(i, field);
  const style = {
    background: color || (primary ? (theme.accent || '#2DD4BF') : 'transparent'),
    color: primary || color ? '#fff' : (theme.ink || '#0E0E23'),
    border: primary || color ? 'none' : `1px solid ${theme.ink || '#0E0E23'}`,
    borderRadius: sharp ? 0 : (theme.radius || 9999),
    fontFamily: theme.bodyFont || 'inherit',
  };
  const className = "inline-flex items-center px-7 py-3 text-sm font-bold tracking-wider uppercase transition-all hover:opacity-90 wb-sortable-item cursor-pointer";
  if (onEdit) {
    return (
      <span {...attrs} className={className} style={style}>
        <InlineText
          blockId={block.id}
          onEdit={onEdit}
          field={`${field}.${i}.text`}
          value={btn?.text || ''}
          tag="span"
          placeholder="Button"
        />
      </span>
    );
  }
  return (
    <a href={btn?.link || '#'} className={className} style={style} {...attrs}>{btn?.text || 'Learn more'}</a>
  );
};

export const Btn = ({ text, link, primary, theme, sharp, buttonIndex, color, field = 'buttons', block, onEdit }) => {
  const className = "inline-block px-7 py-3 text-sm font-bold tracking-wider uppercase transition-all hover:opacity-90 wb-sortable-item";
  const style = {
    background: color || (primary ? (theme.accent || '#2DD4BF') : 'transparent'),
    color: primary || color ? '#fff' : (theme.ink || '#0E0E23'),
    border: primary || color ? 'none' : `1px solid ${theme.ink || '#0E0E23'}`,
    borderRadius: sharp ? 0 : (theme.radius || 9999),
    fontFamily: theme.bodyFont || 'inherit',
  };
  // Iter 175c — Btn becomes inline-editable when an `onEdit` + `block` are
  // wired. Falls back to a plain anchor for public view rendering.
  if (onEdit && block && typeof buttonIndex === 'number') {
    const attrs = getWbButtonAttrs(buttonIndex, field);
    return (
      <span {...attrs} className={`${className} cursor-text`} style={style}>
        <InlineText
          blockId={block.id}
          onEdit={onEdit}
          field={`${field}.${buttonIndex}.text`}
          value={text || 'Learn more'}
          tag="span"
          placeholder="Button"
        />
      </span>
    );
  }
  return (
    <a href={link || '#'} className={className} {...getWbButtonAttrs(buttonIndex, field)} style={style}>{text || 'Learn more'}</a>
  );
};

export const H1 = ({ children, theme, size = 'text-5xl md:text-6xl', testid, block, onEdit, field = 'title', value }) => {
  // Iter 175c — when an `onEdit` is wired through the premium chain we
  // upgrade H1 to inline-editable so the parent ext block does not need
  // any call-site changes. Falls back to a plain heading when `onEdit`
  // is undefined (public view).
  if (onEdit && block) {
    return (
      <InlineText
        blockId={block.id}
        onEdit={onEdit}
        field={field}
        value={(value !== undefined ? value : children) || ''}
        tag="h1"
        className={`${size} font-bold leading-[1.05] tracking-tight`}
        fieldStyle={{ fontFamily: theme.headingFont || 'Syne, sans-serif', color: theme.ink }}
        placeholder="Headline"
      />
    );
  }
  return (
    <h1 data-testid={testid} className={`${size} font-bold leading-[1.05] tracking-tight`} style={{ fontFamily: theme.headingFont || 'Syne, sans-serif', color: theme.ink }}>{children}</h1>
  );
};

export const Eyebrow = ({ children, theme, testid, block, onEdit, field = 'eyebrow', value }) => {
  // Iter 175c — inline-editable when wired. Returns null when both the
  // current value and the children are empty, matching the previous
  // behaviour (hide the slot unless something is there to render).
  const v = value !== undefined ? value : children;
  // Empty optional slot renders nothing in Edit too (Squarespace parity) —
  // add it from the section's Edit panel.
  if (!v) return null;
  if (onEdit && block) {
    return (
      <InlineText
        blockId={block.id}
        onEdit={onEdit}
        field={field}
        value={v || ''}
        tag="p"
        className="text-xs uppercase tracking-[0.22em] opacity-70 mb-3"
        fieldStyle={{ color: theme.ink }}
        placeholder="Eyebrow (optional)"
      />
    );
  }
  return v ? <p data-testid={testid} className="text-xs uppercase tracking-[0.22em] opacity-70 mb-3" style={{ color: theme.ink }}>{v}</p> : null;
};

// Iter 162 — per-block testid factory used by useFluidTextDrag.
export const tidFor = (block) => (field) => block?.id ? `inline-${block.id}-${field}` : undefined;


// Iter 198d — Focal Point Everywhere.
// Squarespace parity: every image in every premium ext block honours the
// section's focal point so the vendor's chosen crop is preserved at every
// aspect ratio. Falls back to `center` when no focal point is set.
//
// Usage in a renderer:
//   <img src={content.image} className="object-cover w-full h-full" style={focalStyle(content)} />
//
// The helper also accepts a per-item override so grids (gallery, features,
// team, etc.) can support item-level focal points once they're added to the
// schema — pass the item as the second arg.
export const focalStyle = (contentOrItem, item) => {
  const src = item?.focus_point ? item : contentOrItem;
  const fp = src?.focus_point;
  if (!fp) return { objectPosition: 'center' };
  const x = typeof fp.x === 'number' ? fp.x : 50;
  const y = typeof fp.y === 'number' ? fp.y : 50;
  return { objectPosition: `${x}% ${y}%` };
};

// Merge helper — combines focal style with any existing inline style prop
// so we never clobber the caller's other styles.
export const withFocal = (baseStyle, content, item) => {
  const focal = focalStyle(content, item);
  return { ...(baseStyle || {}), ...focal };
};
